import { useState } from "react";
import { Check, Loader2, MapPin, Upload } from "lucide-react";
import { toast } from "sonner";
import { apiError, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export const FileField = ({ label, hint, value, onChange, purpose = "KYC_DOCUMENT", testId }) => {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await uploadFile(file, purpose);
      onChange(data.file_id);
      setName(data.original_name);
      toast.success(`${label} uploaded securely`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <label
        className={`mt-1.5 flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed p-4 transition-colors ${
          value ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-emerald-400"
        }`}
      >
        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={handle} data-testid={testId} />
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />
        ) : value ? (
          <Check className="h-5 w-5 text-emerald-600" />
        ) : (
          <Upload className="h-5 w-5 text-slate-400" />
        )}
        <span className="min-w-0 flex-1 text-sm text-slate-600">
          {value ? name || "Document stored privately" : hint || "Tap to upload (JPG, PNG or PDF, max 8MB)"}
        </span>
      </label>
    </div>
  );
};

export const GeoField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);

  const capture = () => {
    setBusy(true);
    if (!navigator.geolocation) {
      onChange({ latitude: 6.5244, longitude: 3.3792, accuracy: 500, label: "Manual fallback (Lagos)" });
      setBusy(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          label: "Device location",
        });
        setBusy(false);
        toast.success("Location captured");
      },
      () => {
        onChange({ latitude: 6.5244, longitude: 3.3792, accuracy: 500, label: "Manual fallback (Lagos)" });
        setBusy(false);
        toast.message("Using fallback coordinates", { description: "Browser location was unavailable." });
      },
      { timeout: 8000 }
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <MapPin className="h-4 w-4 text-emerald-700" />
          {value ? `${value.latitude.toFixed(4)}, ${value.longitude.toFixed(4)}` : "Geolocation not captured yet"}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={capture} disabled={busy} data-testid={testId}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : value ? "Recapture" : "Capture location"}
        </Button>
      </div>
    </div>
  );
};

export const Stepper = ({ steps, current }) => (
  <div className="flex items-center gap-2" data-testid="kyc-stepper">
    {steps.map((s, i) => (
      <div key={s} className="flex flex-1 items-center gap-2">
        <div
          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
            i < current ? "bg-emerald-600 text-white" : i === current ? "bg-emerald-900 text-white" : "bg-slate-200 text-slate-500"
          }`}
        >
          {i < current ? <Check className="h-3.5 w-3.5" /> : i + 1}
        </div>
        <span className={`hidden text-xs font-medium sm:block ${i === current ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
        {i < steps.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
      </div>
    ))}
  </div>
);
